/**
 * EventCard コンポーネント
 *
 * イベント情報をカード形式で表示。
 * 代表画像、イベント名、開催日、種別、場所を表示。
 * カテゴリ別に色分けされたバッジを表示。
 *
 */

import { motion, useReducedMotion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import type { Event } from '@/types/domain';
import { getLocalizedText } from '@/lib/i18n-helpers';
import { formatDate, formatDateRange } from '@/lib/date-formatters';
import { generateSlug } from '@/lib/data-loader';
import { useLanguageStore } from '@/stores/language-store';
import { cn } from '@/lib/cn';
import { getGenreBackgroundClass } from '@/lib/genre-colors';

/**
 * EventCard コンポーネントのProps
 */
export interface EventCardProps {
  /** イベントデータ */
  event: Event;

  /** クリック時のコールバック（任意） */
  onClick?: (id: string) => void;

  /** コンパクト表示 */
  compact?: boolean;

  /** 戻り先URL（未指定の場合は現在のURLを使用） */
  returnTo?: string;
}

/**
 * カテゴリ名の翻訳
 */
/**
 * EventCard コンポーネント
 *
 * 使用例:
 * ```tsx
 * <EventCard event={eventData} onClick={(id) => navigate(`/events/${id}`)} />
 * ```
 */
export function EventCard({
  event,
  onClick,
  compact = false,
  returnTo,
}: EventCardProps) {
  const { language } = useLanguageStore();
  const routerLocation = useLocation();
  const shouldReduceMotion = useReducedMotion();

  const handleClick = () => {
    onClick?.(event.id);
  };

  const slug = generateSlug(event.id);
  const name = getLocalizedText(event.title, language);
  const eventType = event.eventType;
  const eventLocation = getLocalizedText(event.location, language);
  // 日付フォーマット
  const dateDisplay = event.endDate
    ? formatDateRange(event.startDate, event.endDate, language)
    : formatDate(event.startDate, language);

  return (
    <motion.article
      whileHover={shouldReduceMotion ? undefined : { scale: 1.03 }}
      transition={{ duration: 0.2 }}
      className="group overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md"
      aria-label={name}
    >
      <Link
        to={`/events/${slug}`}
        state={{
          from:
            returnTo ?? `${routerLocation.pathname}${routerLocation.search}`,
        }}
        onClick={handleClick}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {/* 代表画像 */}
        <div
          className={cn(
            compact ? 'aspect-square' : 'aspect-video',
            event.image ? 'bg-black' : 'bg-muted'
          )}
        >
          <img
            key={`${event.id}:${event.image || ''}`}
            src={event.image || '/images/noimage.jpg'}
            alt={name}
            className="h-full w-full object-contain"
            loading="lazy"
            width="640"
            height="360"
            onError={(e) => {
              const image = e.currentTarget;
              if (image.getAttribute('src') !== '/images/noimage.jpg') {
                image.src = '/images/noimage.jpg';
              }
            }}
          />
        </div>

        {/* カード情報 */}
        <div className={compact ? 'p-3' : 'p-4'}>
          {/* イベント名 */}
          <h3
            className={cn(
              'line-clamp-2 font-semibold text-foreground',
              compact ? 'text-sm' : 'text-lg'
            )}
          >
            {name}
          </h3>

          {/* メタ情報 */}
          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            {/* 日付 */}
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{dateDisplay}</span>
            </div>

            {/* イベント種別 */}
            {eventType && (
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 01-2.07-.655zM16.44 15.98a4.97 4.97 0 002.07-.654.78.78 0 00.357-.442 3 3 0 00-4.308-3.517 6.484 6.484 0 011.907 3.96 2.32 2.32 0 01-.026.654zM18 8a2 2 0 11-4 0 2 2 0 014 0zM5.304 16.19a.844.844 0 01-.277-.71 5 5 0 019.947 0 .843.843 0 01-.277.71A6.975 6.975 0 0110 18a6.974 6.974 0 01-4.696-1.81z" />
                </svg>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    getGenreBackgroundClass(eventType, false)
                  )}
                >
                  {eventType}
                </span>
              </div>
            )}

            {/* 開催場所 */}
            {eventLocation && (
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="line-clamp-1">{eventLocation}</span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
